import {Component, Input, OnInit} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { BaseButtonComponent } from '../buttons/base-button.component';
import {TranslateService} from '../../services/translate.service';
import {LinkButtonComponent} from '../buttons/link-button.component';
import {BorderIconButtonComponent} from '../buttons/border-icon-button.component';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {GiftcardPreviewDialogComponent} from '../dialogs/giftcard-preview-dialog.component';
import {ModalService} from '../../services/modal.service';

export interface GiftcardPersonalizedFormData {
  recipient?: string;
  message?: string;
  sender?: string;
  amount: number;
  templateId: string;
}

@Component({
  selector: 'app-giftcard-personalized-form',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    ActionButtonComponent,
    BaseButtonComponent,
    LinkButtonComponent,
    BorderIconButtonComponent
  ],
  template: `
    <div class="flex flex-col pb-6">
      <!-- Title -->
      <div class="font-bold mb-6 text-theme-text2">{{getString('Skicka med ett personligt meddelande på presenkortet')}}</div>

      <!-- Form -->
      <div class="flex flex-col gap-6">
        <!-- Recipient -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-bold">{{getString('Till')}}</label>
          <input
            type="text"
            [(ngModel)]="sheetData.recipient"
            class="text-input"
            [placeholder]="'Mottagarens namn'"
          >
        </div>

        <!-- Message -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-bold">{{getString('Meddelande')}}</label>
          <textarea
            [(ngModel)]="sheetData.message"
            rows="3"
            class="text-input"
            [placeholder]="'Skriv ditt meddelande här'"
            maxlength="110"
          ></textarea>
            <div class="text-xs text-theme-text3">
              {{ (sheetData.message?.length || 0) }} / 110 tecken
            </div>
        </div>

        <!-- Sender -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-bold">{{getString('Från')}}</label>
          <input
            type="text"
            [(ngModel)]="sheetData.sender"
            class="text-input"
            [placeholder]="'Ditt namn'"
          >
        </div>
      </div>

      <!-- Warning info -->
      <div class="flex flex-col pt-2">
        <div *ngIf="!sheetData?.recipient?.trim()" class="text-theme-warning">
            Mottagarens namn är obligatoriskt.
        </div>
        <div *ngIf="!sheetData?.message?.trim()" class="text-theme-warning">
            Meddelande kan inte vara tomt.
        </div>
        <div *ngIf="!sheetData?.sender?.trim()" class="text-theme-warning">
          Ditt namn är obligatoriskt.
        </div>
      </div>
      <!-- Preview Button -->
      <div class="mt-6 mb-6 px-4">
        <app-border-icon-button
          [text]="'Förhandsgranska'"
          icon="fa-image"
          (onClick)="showPreview()"
        ></app-border-icon-button>
      </div>

      <!-- Submit Button -->
      <div class="mt-auto px-4">
        <app-action-button
          [text]="'Klar'"
          (onClick)="submit()"
          [disabled]="!isValid()"
          [spinner]="isWorking"
        ></app-action-button>
      </div>
    </div>
  `
})
export class GiftcardPersonalizedFormComponent implements OnInit {
  @Input() sheetData!: GiftcardPersonalizedFormData;
  isWorking: boolean = false;

  constructor(private translateService: TranslateService, private sheetService: BottomSheetService, private modalService: ModalService) {}

  ngOnInit(): void {}

  isValid(): boolean {
    return !!(
      this.sheetData?.recipient?.trim() &&
      this.sheetData?.message?.trim() &&
      this.sheetData?.sender?.trim() &&
      this.sheetData?.message?.length <= 110
    );
  }

  submit(): void {
    if (!this.isValid()) return;
    if (this.sheetData.sender) {
      localStorage.setItem("sender_name", this.sheetData.sender);
    }
    if (this.sheetData.recipient) {
      localStorage.setItem("receiver_name", this.sheetData.recipient);
    }
    if (this.sheetData.message) {
      localStorage.setItem("message", this.sheetData.message);
    }

    this.sheetService.clearStack();
    // @ts-ignore
    this.sheetData.callback(this.sheetData);
  }

  getString(s: string) {
    return this.translateService.getString(s);
  }

  // Add a preview button that calls this method
  showPreview() {
    if (!this.isValid()) return;
    GiftcardPreviewDialogComponent.showGiftcardPreview(
      this.modalService,
      this.sheetData.templateId,
      this.sheetData.sender!,
      this.sheetData.recipient!,
      this.sheetData.message!,
      this.sheetData.amount
    );
  }
}
